import { useCallback, useEffect, useState } from "react";
import { useAccount, usePublicClient, useSendTransaction, useSwitchChain } from "wagmi";
import { encodeDeployData, formatEther, parseUnits } from "viem";
import { Rocket, Copy, ExternalLink, CheckCircle2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import artifacts from "../lib/hiveTokenArtifacts.json";
import { api, errMsg } from "../lib/api";
import { addrUrl } from "../lib/chains";
import { TxStatus, runTx } from "./TxStatus";
import { useApp } from "../context/AppContext";
import { LaunchChecklist } from "./LaunchChecklist";
import { LaunchStatus } from "./LaunchStatus";
import { useLaunchFee, LaunchFeeStep } from "./LaunchFee";
import { ConnectMenu } from "./WalletButton";

const PENDING_KEY = "hive_pending_launch";
const PLACEHOLDER = "0x000000000000000000000000000000000000dEaD";

export const variantOf = (d) => (d.burnable ? "HiveBurnableToken" : "HiveToken");

export const deployData = (d, recipient) => {
  const c = artifacts.contracts[variantOf(d)];
  return encodeDeployData({ abi: c.abi, bytecode: c.bytecode, args: [d.name.trim(), d.symbol, Number(d.decimals), parseUnits(d.total_supply, Number(d.decimals)), recipient] });
};

export const useDeployEstimate = (d, chainId) => {
  const pc = usePublicClient({ chainId });
  const { address } = useAccount();
  const [est, setEst] = useState(null);
  useEffect(() => {
    let live = true;
    setEst(null);
    (async () => {
      try {
        const from = address || PLACEHOLDER;
        const [gas, price] = await Promise.all([pc.estimateGas({ account: from, data: deployData(d, from) }), pc.getGasPrice()]);
        live && setEst({ gas, price, cost: (gas * price * 12n) / 10n });
      } catch (e) { live && setEst({ error: e.shortMessage || e.message }); }
    })();
    return () => { live = false; };
  }, [pc, d, address]);
  return est;
};

const Success = ({ rec }) => {
  const copy = () => navigator.clipboard.writeText(rec.address).then(() => toast.success("Address copied"));
  return (
    <div data-testid="launch-success" className="space-y-4">
      <p className="flex items-center gap-2 text-lime font-semibold"><CheckCircle2 className="h-5 w-5" /> {rec.symbol} deployed and confirmed on {rec.network} (block {rec.block_number})</p>
      <LaunchStatus token={rec} />
      {rec.duplicate_of ? <p data-testid="launch-duplicate-warning" className="rounded-lg border border-danger/40 bg-danger/10 p-3 text-xs text-danger">Deployed on-chain, but not listed on HIVEVORA: {rec.duplicate_of}</p> : <LaunchChecklist token={rec} />}
      <div className="grid sm:grid-cols-2 gap-x-8 text-sm">
        {[["Contract", rec.address], ["Name", rec.name], ["Symbol", rec.symbol], ["Decimals", rec.decimals], ["Total supply (base units)", rec.total_supply], ["Template", rec.variant], ["Transaction", rec.tx_hash]].map(([k, val]) => (
          <div key={k} className="flex justify-between gap-3 py-2 border-b border-white/5"><span className="text-slate-500">{k}</span><span className="font-mono text-slate-100 break-all text-right">{String(val)}</span></div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="launch-copy-address" onClick={copy} className="rounded-lg border border-lime/30 px-3 py-2 text-xs text-lime hover:bg-lime/10 inline-flex items-center gap-1"><Copy className="h-3.5 w-3.5" /> Copy address</button>
        <a data-testid="launch-explorer-link" href={`${rec.explorer}/address/${rec.address}`} target="_blank" rel="noreferrer" className="rounded-lg border border-white/10 px-3 py-2 text-xs inline-flex items-center gap-1 hover:text-lime">Block explorer <ExternalLink className="h-3.5 w-3.5" /></a>
        {rec.chain_id === 4663 && <Link data-testid="launch-token-page-link" to={`/token/${rec.address}`} className="rounded-lg border border-white/10 px-3 py-2 text-xs hover:text-lime">Open token page</Link>}
      </div>
      <p className="text-[11px] text-slate-500">Manual verification: on the explorer contract page choose “Verify & publish” → Solidity (Standard JSON input), compiler {rec.template_version === 1 ? "v0.8.24+commit.e11b9ed9" : artifacts.compiler}, contract <span className="font-mono">{rec.variant}</span>, upload <a className="text-lime hover:underline" href={`/contracts/${rec.template_version === 1 ? "HiveToken.standard-input.json" : `HiveToken.v${rec.template_version || artifacts.template_version}.standard-input.json`}`} download>the standard-JSON input</a> (optimizer 200 runs, viaIR off, EVM paris). Source: OpenZeppelin Contracts {artifacts.openzeppelin} (MIT).</p>
    </div>
  );
};

export const LaunchDeploy = ({ d, chainId, est, extra: baseExtra }) => {
  let extra = baseExtra;
  const { address, isConnected, chainId: walletChain } = useAccount();
  const { user, signIn, signing } = useApp();
  const pc = usePublicClient({ chainId });
  const { sendTransactionAsync } = useSendTransaction();
  const { switchChainAsync, isPending: switching } = useSwitchChain();
  const [tx, setTx] = useState(null);
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const fee = useLaunchFee(chainId, user);

  const record = useCallback(async (p) => {
    for (let i = 0; i < 6; i++) {
      try { const r = await api.post("/launch/record", p); localStorage.removeItem(PENDING_KEY); setRec(r.data); return; }
      catch (e) { if (e?.response?.status !== 409) { setTx((t) => ({ ...t, stage: "error", error: `Deployed, but HIVEVORA could not register it: ${errMsg(e)}` })); return; } await new Promise((r) => setTimeout(r, 4000)); }
    }
  }, []);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    if (!saved || !user || saved.creator !== user.address || saved.chain_id !== chainId) return;
    setBusy(true);
    setTx({ stage: "pending", label: "Deploy (resumed)", hash: saved.tx_hash, chainId });
    pc.waitForTransactionReceipt({ hash: saved.tx_hash, timeout: 180000 })
      .then((rc) => rc.status === "success" ? record(saved) : (localStorage.removeItem(PENDING_KEY), setTx({ stage: "failed", label: "Deploy", hash: saved.tx_hash, chainId })))
      .catch((e) => setTx({ stage: "error", label: "Deploy", hash: saved.tx_hash, chainId, error: e.shortMessage }))
      .finally(() => setBusy(false));
  }, [user, chainId, pc, record]);

  if (rec) return <Success rec={rec} />;
  if (!isConnected) return <div className="flex items-center gap-3"><span className="text-sm text-slate-400">Connect the wallet that will deploy and receive the supply.</span><ConnectMenu testid="launch-connect-wallet" /></div>;
  if (!user) return <button data-testid="launch-sign-in" disabled={signing} onClick={signIn} className="btn-lime px-5 py-2.5 text-sm">{signing ? "Check your wallet…" : "Sign in (free signature) to register your launch"}</button>;
  if (walletChain !== chainId) return <button data-testid="launch-switch-network" disabled={switching} onClick={() => switchChainAsync({ chainId }).catch((e) => toast.error(e.shortMessage || e.message))} className="btn-lime px-5 py-2.5 text-sm">Switch wallet to {chainId === 4663 ? "Robinhood Chain (4663)" : "Robinhood Chain Testnet (46630)"}</button>;

  const deploy = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!fee.ready) return;
      const data = deployData(d, address);
      extra = { ...extra, fee_id: fee.paid?.id };
      const rc = await runTx({
        pc, setTx, chainId, label: `Deploy ${d.symbol}`,
        send: async () => {
          const hash = await sendTransactionAsync({ chainId, data });
          localStorage.setItem(PENDING_KEY, JSON.stringify({ tx_hash: hash, chain_id: chainId, variant: variantOf(d), creator: user.address, ...extra }));
          return hash;
        },
      });
      if (rc) await record({ tx_hash: rc.transactionHash, chain_id: chainId, variant: variantOf(d), ...extra });
      else localStorage.removeItem(PENDING_KEY);
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="launch-deploy-panel">
      <p className="text-sm text-slate-300">Your wallet <span className="font-mono text-lime">{address.slice(0, 6)}…{address.slice(-4)}</span> will sign and pay for a contract-creation transaction. The full supply is minted to this wallet. Estimated cost: <b className="text-white font-mono">{est?.cost ? `${Number(formatEther(est.cost)).toFixed(6)} ETH` : est?.error ? "estimate failed" : "…"}</b> {fee.required && fee.required_usd ? `, plus the $${fee.required_usd} launch & profile fee paid first` : chainId === 4663 ? " (no HIVEVORA fee)" : " (testnet launches are free)"}.</p>
      <LaunchFeeStep fee={fee} pc={pc} />
      {est?.error && <p className="text-xs text-danger">Gas estimate failed: {est.error}. Deployment would likely fail — check your inputs and ETH balance.</p>}
      <button data-testid="launch-deploy-button" disabled={busy || !!est?.error || !fee.ready} onClick={deploy} className="btn-lime px-6 py-3 text-sm disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} {busy ? "Deploying…" : fee.required && !fee.paid ? "Step 2 · Deploy (pay the fee first)" : `Deploy ${d.symbol} with my wallet`}
      </button>
      <TxStatus tx={tx} testid="launch-tx-status" />
      <a href={addrUrl(address, chainId)} target="_blank" rel="noreferrer" className="block text-[11px] text-slate-500 hover:text-lime">View your wallet on the explorer</a>
    </div>
  );
};
